import type { ComponentType, SVGProps } from 'react';
import { Badge } from '@radix-ui/themes';
import type { RoleType } from '@echo-portal/shared';
import {
  EyeOpenIcon,
  GearIcon,
  MagnifyingGlassIcon,
  Pencil1Icon,
} from '@/components/icons';

interface RoleBadgeProps {
  role: RoleType;
  size?: 'sm' | 'md' | 'lg';
  showIcon?: boolean;
}

// Icons replace the previous emoji set so the badge matches the rest
// of the iconography (Boxicons via the @/components/icons shim) and
// inherits the badge's text colour through `currentColor`. Picked to
// echo each role's verb: eye → viewing, pencil → writing, magnifier
// → reviewing, gear → administration.
type RoleConfig = {
  label: string;
  color: 'gray' | 'blue' | 'purple' | 'red';
  Icon: ComponentType<SVGProps<SVGSVGElement>>;
};

const roleConfig: Record<RoleType, RoleConfig> = {
  viewer: {
    label: 'Viewer',
    color: 'gray',
    Icon: EyeOpenIcon,
  },
  contributor: {
    label: 'Contributor',
    color: 'blue',
    Icon: Pencil1Icon,
  },
  reviewer: {
    label: 'Reviewer',
    color: 'purple',
    Icon: MagnifyingGlassIcon,
  },
  administrator: {
    label: 'Administrator',
    color: 'red',
    Icon: GearIcon,
  },
};

const sizeMap = {
  sm: '1' as const,
  md: '2' as const,
  lg: '3' as const,
};

// Badge text scales with size; the icon needs to track it so it never
// dwarfs or shrinks against the label.
const iconSizeMap: Record<NonNullable<RoleBadgeProps['size']>, number> = {
  sm: 12,
  md: 14,
  lg: 16,
};

export function RoleBadge({ role, size = 'md', showIcon = true }: RoleBadgeProps) {
  const config = roleConfig[role];

  if (!config) {
    return null;
  }

  const { Icon } = config;
  const iconSize = iconSizeMap[size];

  return (
    <Badge
      color={config.color}
      variant="soft"
      size={sizeMap[size]}
      radius="full"
      title={`Role: ${config.label}`}
    >
      {showIcon && (
        <Icon
          width={iconSize}
          height={iconSize}
          aria-hidden="true"
        />
      )}
      <span>{config.label}</span>
    </Badge>
  );
}

export default RoleBadge;
